<template>
  <b-card-code title="Editar Lista de Horarios" no-body v-if="!isFetching">
    <b-card-body>
      <div class="d-flex justify-content-between flex-wrap">
        <b-form-group label-align-sm="left" label-cols-sm="2" label-for="sortBySelect">
          <h5>Ordenar</h5>
          <b-input-group>
            <b-form-select id="sortBySelect" v-model="sortBy" :options="sortOptions">
              <template #first>
                <option value="">None</option>
              </template>
            </b-form-select>
            <b-form-select v-model="sortDesc" :disabled="!sortBy">
              <option :value="false">Asc</option>
              <option :value="true">Desc</option>
            </b-form-select>
          </b-input-group>
        </b-form-group>
      </div>

      <!-- EDITAR FECHAS DEL TIMETABLE -->
      <b-row class="mt-2">
        <b-col md="6">
          <b-form-group>
            <h5>Fecha Inicio (Horario)</h5>
            <flat-pickr v-model="timetable.datetime_start" class="form-control" :config="dateTimeConfig"
              :disabled="disable || isSavingTimetable" />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group>
            <h5>Fecha Final (Horario)</h5>
            <flat-pickr v-model="timetable.datetime_end" class="form-control" :config="dateTimeConfig"
              :disabled="disable || isSavingTimetable" />
          </b-form-group>
        </b-col>

        <b-col cols="12" class="d-flex justify-content-end">
          <b-button variant="primary" class="mt-1" @click="saveTimetableDates" :disabled="disable || isSavingTimetable">
            <feather-icon icon="SaveIcon" class="mr-1" />
            Guardar fechas
          </b-button>
        </b-col>
      </b-row>
    </b-card-body>

    <b-row class="justify-content-center mt-3">
      <b-col cols="auto">
        <b-button variant="outline-primary" class="mb-1" :to="{ name: 'schedules' }">
          <feather-icon icon="ArrowLeftCircleIcon" class="mr-1" />
          Volver Atrás
        </b-button>
      </b-col>
    </b-row>

    <b-table borderless striped hover responsive :per-page="perPage" :current-page="currentPage" :items="items"
      :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :sort-direction="sortDirection">
      <template #cell(hora_inici)="data">
        <div v-if="data.item.editing">
          <input type="time" v-model="data.item.hora_inici_edit" class="form-control" />
        </div>
        <div v-else>
          {{ getTimeString(data.value) }}
        </div>
      </template>

      <template #cell(hora_fi)="data">
        <div v-if="data.item.editing">
          <input type="time" v-model="data.item.hora_fi_edit" class="form-control" />
        </div>
        <div v-else>
          {{ getTimeString(data.value) }}
        </div>
      </template>

      <template #cell(actions)="data">
        <div class="text-nowrap">
          <button v-if="!data.item.editing" @click="startEditing(data.item)" class="btn btn-sm btn-outline-primary">
            Editar
          </button>
          <template v-else>
            <button @click="saveItem(data.item)" class="btn btn-sm btn-success mr-1">
              Guardar
            </button>
            <button @click="cancelEditing(data.item)" class="btn btn-sm btn-outline-secondary">
              Cancelar
            </button>
          </template>
        </div>
      </template>
    </b-table>

    <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
      <b-form-group label="Por página" label-cols="6" label-align="left" label-size="sm" label-for="sortBySelect"
        class="text-nowrap mb-md-0 mr-1">
        <b-form-select id="perPageSelect" v-model="perPage" size="sm" inline :options="pageOptions" />
      </b-form-group>

      <div>
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" first-number last-number
          prev-class="prev-item" next-class="next-item" class="mb-0">
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </b-card-body>
  </b-card-code>
</template>

<script>
import BCardCode from "@core/components/b-card-code/BCardCode.vue";
import {
  BTable,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BRow,
  BCol,
  BButton,
  BCardBody,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import axios from "@/libs/axios";
import moment from "moment";

export default {
  components: {
    BCardCode,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BRow,
    BCol,
    BButton,
    BCardBody,
    flatPickr,
  },

  data() {
    return {
      isFetching: true,
      disable: false,
      isSavingTimetable: false,

      perPage: 10,
      pageOptions: [5, 10, 20, 50],
      totalRows: 1,
      currentPage: 1,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",

      // OJO: aquí queremos Date para flatpickr
      timetable: {
        pkid_timetable: null,
        datetime_start: null,
        datetime_end: null,
      },

      // picker
      dateTimeConfig: {
        enableTime: true,
        time_24hr: true,
        altInput: true,
        altFormat: "d/m/Y H:i",
        dateFormat: "Y-m-d H:i",
        locale: { firstDayOfWeek: 1 },
      },

      items: [],
      fields: [
        { key: "actions", label: "Acciones" },
        { key: "Dia_setmana", label: "Día de la semana", sortable: true },
        { key: "hora_inici", label: "Tiempo Inicial", sortable: true },
        { key: "hora_fi", label: "Tiempo Final", sortable: true },
      ],
    };
  },

  computed: {
    sortOptions() {
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },
  },

  methods: {
    // ===== helpers horarios =====
    getTimeString(value) {
      if (!value) return "";
      if (typeof value === "string" && value.includes("T")) return value.split("T")[1].split(".")[0];
      return value;
    },
    getTimeForInput(value) {
      if (!value) return "";
      let timePart = value;
      if (typeof value === "string" && value.includes("T")) timePart = value.split("T")[1].split(".")[0];
      return String(timePart).split(":").slice(0, 2).join(":");
    },

    startEditing(item) {
      this.$set(item, "_original", { ...item });
      this.$set(item, "editing", true);
      this.$set(item, "hora_inici_edit", this.getTimeForInput(item.hora_inici));
      this.$set(item, "hora_fi_edit", this.getTimeForInput(item.hora_fi));
    },

    async saveItem(item) {
      try {
        const payload = {
          fkid_timetable: item.fkid_timetable,
          Dia_setmana: item.Dia_setmana,
          hora_inici: item.hora_inici_edit + ":00",
          hora_fi: item.hora_fi_edit + ":00",
        };

        await axios.put("/schedules", payload);

        item.hora_inici = payload.hora_inici;
        item.hora_fi = payload.hora_fi;
        item.editing = false;

        this.$bvToast.toast("Horario actualizado correctamente", {
          title: "Éxito",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error("Error al guardar:", error);
        this.$bvToast.toast(error.response?.data?.Error || "Error al guardar", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        this.cancelEditing(item);
      }
    },

    cancelEditing(item) {
      Object.assign(item, item._original);
      item.editing = false;
      delete item._original;
    },

    // Convierte "2025-05-06T02:00:00.000Z" => Date(2025, 4, 6, 2, 0, 0) en LOCAL
    toPickerNaiveDate(dbIsoString) {
      if (!dbIsoString) return null;

      // coge la parte sin Z y sin ms: "2025-05-06T02:00:00"
      const base = String(dbIsoString).replace("Z", "").split(".")[0];
      // base: "2025-05-06T02:00:00"
      const [datePart, timePart = "00:00:00"] = base.split("T");
      const [y, m, d] = datePart.split("-").map(Number);
      const [hh, mm, ss] = timePart.split(":").map(Number);

      const localDate = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0);

      return localDate;
    },

    // Para guardar EXACTAMENTE lo que el usuario ve (sin utc()):
    toLocalSqlStringFromPickerDate(dateObj) {
      if (!dateObj) return null;
      const out = moment(dateObj).format("YYYY-MM-DD HH:mm:ss");
      return out;
    },

    async saveTimetableDates() {
      const id = this.timetable.pkid_timetable;
      if (!id) return;

      const startDate = this.timetable.datetime_start; // Date (local)
      const endDate = this.timetable.datetime_end;     // Date (local)

      if (startDate && endDate && moment(endDate).isBefore(moment(startDate))) {
        this.$bvToast.toast("La fecha final no puede ser menor que la fecha inicio", {
          title: "Validación",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.isSavingTimetable = true;
      try {
        const payload = {
          pkid_timetable: id,
          datetime_start: this.toLocalSqlStringFromPickerDate(startDate),
          datetime_end: this.toLocalSqlStringFromPickerDate(endDate),
        };

        await axios.put("/timetable", payload);

        this.$bvToast.toast("Fechas del horario actualizadas correctamente", {
          title: "Éxito",
          variant: "success",
          solid: true,
        });
      } catch (e) {
        console.error("Error guardando timetable:", e);
        this.$bvToast.toast(e.response?.data?.Error || "Error al guardar las fechas del horario", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isSavingTimetable = false;
      }
    },
  },

  async created() {
    const pkid_timetable = this.$route.params.id;
    if (!pkid_timetable) {
      this.isFetching = false;
      return;
    }

    try {
      // 1) schedules
      const resSchedules = await axios.get(`/schedules/${pkid_timetable}`);
      this.items = (resSchedules.data || []).filter(
        (s) => String(s.fkid_timetable) === String(pkid_timetable)
      );
      this.totalRows = this.items.length;

      // 2) timetable (GET /timetable y filtramos)
      const resAll = await axios.get("/timetable");
      const t = (resAll.data || []).find((x) => String(x.pkid_timetable) === String(pkid_timetable));

      this.timetable.pkid_timetable = Number(pkid_timetable);

      this.timetable.datetime_start = this.toPickerNaiveDate(t?.datetime_start);
      this.timetable.datetime_end = this.toPickerNaiveDate(t?.datetime_end);

    } catch (error) {
      this.$bvToast.toast("Fallo al conectar", {
        title: `Error: ${error}`,
        variant: "danger",
        solid: true,
      });
    }

    this.isFetching = false;
  },

  mounted() {
    if (localStorage.getItem("accessToken") === null) {
      this.$router.go(0);
    }
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>